// Serwis API do komunikacji z backendem
// W produkcji korzystamy z Netlify Functions (/api) połączonych z Supabase.
// Lokalnie: netlify dev (8888) lub npm run server (3001).
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');

const ADMIN_TOKEN_KEY = 'adminToken';

function getAdminToken() {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token) {
  if (token) {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
}

export function clearAdminToken() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function hasAdminToken() {
  return !!getAdminToken();
}

class ApiService {
  async request(endpoint, options = {}, { admin = false } = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (admin) {
      const token = getAdminToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const config = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (response.status === 401 && admin) {
        clearAdminToken();
        localStorage.removeItem('adminAuth');
        throw new Error('Sesja wygasła. Zaloguj się ponownie.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  async getAuthTotpStatus() {
    try {
      return await this.request('/auth/totp-status');
    } catch {
      return { enabled: false };
    }
  }

  async verifyPassword(username, password, totpCode) {
    const payload = { username, password };
    if (totpCode) payload.totpCode = totpCode;
    const url = `${API_BASE_URL}/auth`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    try {
      return await response.json();
    } catch {
      throw new Error('Błąd połączenia z serwerem');
    }
  }

  async getConfig() {
    const url = `${API_BASE_URL}/config`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 5000) : null;
    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      });
      if (!response.ok) {
        throw new Error('Nie udało się pobrać konfiguracji');
      }
      return await response.json();
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  async getEvents() {
    return this.request('/events');
  }

  async getEvent(id) {
    return this.request(`/events/${id}`);
  }

  async createEvent(eventData) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    }, { admin: true });
  }

  async updateEvent(id, eventData) {
    return this.request(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    }, { admin: true });
  }

  async deleteEvent(id) {
    return this.request(`/events/${id}`, {
      method: 'DELETE',
    }, { admin: true });
  }

  async bulkUpdateEvents(events) {
    return this.request('/events/bulk', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }, { admin: true });
  }

  async mergeEvents(events) {
    return this.request('/events/merge', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }, { admin: true });
  }

  async submitEvent(eventData) {
    const url = `${API_BASE_URL}/submissions`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    });
    let data = {};
    try {
      data = await response.json();
    } catch {
      throw new Error('Błąd połączenia z serwerem');
    }
    if (response.status === 429) {
      throw new Error(data.error || 'Osiągnięto limit zgłoszeń. Spróbuj ponownie za godzinę.');
    }
    if (!response.ok) {
      throw new Error(data.error || 'Nie udało się wysłać zgłoszenia');
    }
    return data;
  }

  async getSubmissions() {
    return this.request('/submissions', {}, { admin: true });
  }

  async updateSubmission(id, submissionData) {
    return this.request(`/submissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(submissionData),
    }, { admin: true });
  }

  async approveSubmission(id) {
    return this.request(`/submissions/${id}/approve`, {
      method: 'POST',
    }, { admin: true });
  }

  async rejectSubmission(id) {
    return this.request(`/submissions/${id}/reject`, {
      method: 'POST',
    }, { admin: true });
  }

  async pruneEvents(days = 0) {
    const qs = days && Number.isFinite(days) ? `?days=${days}` : '';
    return this.request(`/maintenance/prune${qs}`, {
      method: 'POST',
    }, { admin: true });
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
